// Offline storage utility for packing lists and templates
// Uses IndexedDB for robust offline storage with fallback to localStorage

const DB_NAME = 'OdyVoyOfflineDB';
const DB_VERSION = 1;
const PACKING_LISTS_STORE = 'packing_lists';
const TEMPLATES_STORE = 'templates';

interface PackingListData {
  id: string;
  destination: string;
  country: string;
  tripType: string;
  categories: any[];
  syncedAt: number;
  updatedAt: number;
}

interface TemplateData {
  id: string;
  name: string;
  tripType: string;
  items: any;
  syncedAt: number;
}

// Initialize IndexedDB
const initDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create object stores if they don't exist
      if (!db.objectStoreNames.contains(PACKING_LISTS_STORE)) {
        db.createObjectStore(PACKING_LISTS_STORE, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(TEMPLATES_STORE)) {
        db.createObjectStore(TEMPLATES_STORE, { keyPath: 'id' });
      }
    };
  });
};

// Packing List Operations
export const savePackingListOffline = async (
  id: string,
  destination: string,
  country: string,
  tripType: string,
  categories: any[]
): Promise<void> => {
  try {
    const db = await initDB();
    const transaction = db.transaction([PACKING_LISTS_STORE], 'readwrite');
    const store = transaction.objectStore(PACKING_LISTS_STORE);

    const packingList: PackingListData = {
      id,
      destination,
      country,
      tripType,
      categories,
      syncedAt: Date.now(),
      updatedAt: Date.now(),
    };

    await new Promise((resolve, reject) => {
      const request = store.put(packingList);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    console.log('Packing list saved offline:', id);
  } catch (error) {
    console.error('Error saving packing list offline:', error);
    // Fallback to localStorage
    try {
      const key = `packing_list_${id}`;
      localStorage.setItem(
        key,
        JSON.stringify({ id, destination, country, tripType, categories, syncedAt: Date.now() })
      );
    } catch (lsError) {
      console.error('LocalStorage fallback failed:', lsError);
    }
  }
};

export const getPackingListOffline = async (id: string): Promise<PackingListData | null> => {
  try {
    const db = await initDB();
    const transaction = db.transaction([PACKING_LISTS_STORE], 'readonly');
    const store = transaction.objectStore(PACKING_LISTS_STORE);

    return new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting packing list offline:', error);
    // Fallback to localStorage
    try {
      const key = `packing_list_${id}`;
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (lsError) {
      console.error('LocalStorage fallback failed:', lsError);
      return null;
    }
  }
};

export const getAllPackingListsOffline = async (): Promise<PackingListData[]> => {
  try {
    const db = await initDB();
    const transaction = db.transaction([PACKING_LISTS_STORE], 'readonly');
    const store = transaction.objectStore(PACKING_LISTS_STORE);

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting all packing lists:', error);
    return [];
  }
};

// Template Operations
export const saveTemplateOffline = async (
  id: string,
  name: string,
  tripType: string,
  items: any
): Promise<void> => {
  try {
    const db = await initDB();
    const transaction = db.transaction([TEMPLATES_STORE], 'readwrite');
    const store = transaction.objectStore(TEMPLATES_STORE);

    const template: TemplateData = {
      id,
      name,
      tripType,
      items,
      syncedAt: Date.now(),
    };

    await new Promise((resolve, reject) => {
      const request = store.put(template);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    console.log('Template saved offline:', id);
  } catch (error) {
    console.error('Error saving template offline:', error);
    // Fallback to localStorage
    try {
      const key = `template_${id}`;
      localStorage.setItem(key, JSON.stringify({ id, name, tripType, items, syncedAt: Date.now() }));
    } catch (lsError) {
      console.error('LocalStorage fallback failed:', lsError);
    }
  }
};

export const getTemplateOffline = async (id: string): Promise<TemplateData | null> => {
  try {
    const db = await initDB();
    const transaction = db.transaction([TEMPLATES_STORE], 'readonly');
    const store = transaction.objectStore(TEMPLATES_STORE);

    return new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting template offline:', error);
    // Fallback to localStorage
    try {
      const key = `template_${id}`;
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (lsError) {
      console.error('LocalStorage fallback failed:', lsError);
      return null;
    }
  }
};

export const getAllTemplatesOffline = async (): Promise<TemplateData[]> => {
  try {
    const db = await initDB();
    const transaction = db.transaction([TEMPLATES_STORE], 'readonly');
    const store = transaction.objectStore(TEMPLATES_STORE);

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting all templates:', error);
    return [];
  }
};

export const deleteTemplateOffline = async (id: string): Promise<void> => {
  try {
    const db = await initDB();
    const transaction = db.transaction([TEMPLATES_STORE], 'readwrite');
    const store = transaction.objectStore(TEMPLATES_STORE);

    await new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error deleting template:', error);
  }
};

// Check if offline mode is available
export const isOfflineAvailable = (): boolean => {
  return 'indexedDB' in window || 'localStorage' in window;
};

// Get offline status
export const isOnline = (): boolean => {
  return navigator.onLine;
};
